import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { onAuthStateChanged } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { auth } from '../../firebase';
import axios from '../../utils/axios';
import { createAuthedThunk } from '../authedThunk';

const mutationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
  errorText: null,
};
const fetchDataInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  data: null,
};

const initialState = {
  createDiscoveryTree: { ...mutationInitialState },
  discoveryTreeStatus: { ...fetchDataInitialState },
  sellingStyles: { ...fetchDataInitialState },
  generateValueProp: { ...mutationInitialState },
  valuePropGenerationStatus: { ...fetchDataInitialState },
  dealDiscoveryRisks: { ...fetchDataInitialState },
  createMeetingPrep: { ...mutationInitialState },
  createMeetingPrepGoals: { ...mutationInitialState },
  orgChart: { ...fetchDataInitialState },
  updateOrgChart: { ...mutationInitialState },
  orgChartForDeal: { ...fetchDataInitialState },
  updateOrgChartForDeal: { ...mutationInitialState },
  valuePropFiles: { ...fetchDataInitialState },
  deleteValuePropFile: { ...mutationInitialState },
  uploadFileUrl: { ...fetchDataInitialState },
  updateValuePropFileTitle: { ...mutationInitialState },
  confirmFileUpload: { ...mutationInitialState },
};

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const thunk = createAuthedThunk;

export const createDiscoveryTree = thunk(
  'discovery/createDiscoveryTree',
  async (token, { payload }) => {
    const r = await axios.post('/us/dt/discovery-tree', payload, {
      params: { requestId: uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to create discovery tree');
  },
);

export const getDiscoveryTreeStatus = thunk(
  'discovery/getDiscoveryTreeStatus',
  async (token, treeId) => {
    const r = await axios.get(`/us/dt/discovery-tree/${treeId}/status`, {
      params: { requestId: uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to get discovery tree status');
  },
  { dedupeKey: (id) => (id ? `treeStatus:${id}` : null) },
);

export const getSellingStyles = thunk(
  'discovery/getSellingStyles',
  async (token) => {
    const r = await axios.get('/us/dt/discovery-tree/selling-styles', {
      params: { requestId: uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to get selling styles');
  },
);

export const generateValueProp = thunk(
  'discovery/generateValueProp',
  async (token, { payload }) => {
    const r = await axios.post('/us/dt/discovery-tree/value-prop', payload, {
      params: { requestId: uuidv4() },
      headers: { Authorization: `Bearer ${token}`, 'x-api-version': 'v2' },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to generate value prop');
  },
);

export const checkValuePropStatus = thunk(
  'discovery/checkValuePropStatus',
  async (token, asyncRequestId) => {
    const r = await axios.get(`/us/dt/async-request/${asyncRequestId}/status`, {
      params: { requestId: uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to check value prop status');
  },
  // Around fifteen components poll this same endpoint. Whenever two of them are
  // waiting on the same job, they now share one request instead of issuing one
  // each per tick.
  { dedupeKey: (id) => (id ? `asyncStatus:${id}` : null) },
);

export const getDealDiscoveryRisks = thunk(
  'discovery/getDealDiscoveryRisks',
  async (token) => {
    const r = await axios.get('/us/dt/discovery-tree/risks', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to get discovery risks');
  },
);

export const createMeetingPrep = thunk(
  'discovery/createMeetingPrep',
  async (token, { dealId, payload }) => {
    const r = await axios.post(`/us/deals/${dealId}/meeting-prep`, payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to create meeting prep');
  },
);

export const createMeetingPrepGoals = thunk(
  'discovery/createMeetingPrepGoals',
  async (token, { dealId, meetingPrepId }) => {
    const r = await axios.post(
      `/us/deals/${dealId}/meeting-prep/${meetingPrepId}`,
      null,
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to create meeting prep goals');
  },
);

export const getOrgChart = thunk(
  'discovery/getOrgChart',
  async (token, accountId) => {
    const r = await axios.get(`/us/js/org-chart/account/${accountId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to get org chart');
  },
);

export const createOrUpdateOrgChart = createAsyncThunk(
  'discovery/createOrUpdateOrgChart',
  async ({ accountId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user)
            return reject(rejectWithValue({ text: null, is400: false }));
          try {
            const token = await user.getIdToken();
            const r = await axios.put(
              `/us/js/org-chart/account/${accountId}`,
              payload,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (r.status === 200) resolve(r.data);
            else reject(rejectWithValue({ text: r.data?.text, is400: false }));
          } catch (e) {
            if (e?.response?.status === 400)
              reject(
                rejectWithValue({ text: e?.response?.data?.text, is400: true }),
              );
            else reject(rejectWithValue({ text: null, is400: false }));
          }
        },
        (error) => reject(rejectWithValue({ text: null, is400: false })),
      );
    });
  },
);

export const getOrgChartForDeal = thunk(
  'discovery/getOrgChartForDeal',
  async (token, dealId) => {
    const r = await axios.get('/us/deals/org-chart/v2', {
      params: { requestId: uuidv4(), dealId },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to get org chart for deal');
  },
);

export const updateOrgChartForDeal = createAsyncThunk(
  'discovery/updateOrgChartForDeal',
  async ({ dealId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user)
            return reject(rejectWithValue({ text: null, is400: false }));
          try {
            const token = await user.getIdToken();
            const r = await axios.put('/us/deals/org-chart/v2', payload, {
              params: { requestId: uuidv4(), dealId },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (r.status === 200) resolve(r.data);
            else reject(rejectWithValue({ text: r.data?.text, is400: false }));
          } catch (e) {
            if (e?.response?.status === 400)
              reject(
                rejectWithValue({ text: e?.response?.data?.text, is400: true }),
              );
            else reject(rejectWithValue({ text: null, is400: false }));
          }
        },
        (error) => reject(rejectWithValue({ text: null, is400: false })),
      );
    });
  },
);

export const getValuePropFiles = thunk(
  'discovery/getValuePropFiles',
  async (token, fileIds) => {
    const r = await axios.post('/us/files', fileIds, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to get value prop files');
  },
);

export const deleteValuePropFile = thunk(
  'discovery/deleteValuePropFile',
  async (token, fileId) => {
    const r = await axios.delete(`/us/files/${fileId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return fileId;
    throw new Error('Failed to delete file');
  },
);

export const getUploadUrlForFile = thunk(
  'discovery/getUploadUrlForFile',
  async (token, payload) => {
    const r = await axios.post('/us/upload/file', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to get upload URL');
  },
);

export const confirmFileUpload = thunk(
  'discovery/confirmFileUpload',
  async (token, fileId) => {
    const r = await axios.put(`/us/upload/file/${fileId}`, null, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to confirm file upload');
  },
);

export const updateValuePropFileTitle = thunk(
  'discovery/updateValuePropFileTitle',
  async (token, { fileId, title }) => {
    const r = await axios.put(
      `/us/files/${fileId}`,
      { title },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to update file title');
  },
);

const addMutationCases = (builder, thunk, key) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[key].processing = true;
      state[key].done = false;
      state[key].failed = false;
      state[key].error = null;
    })
    .addCase(thunk.fulfilled, (state) => {
      state[key].processing = false;
      state[key].done = true;
      state[key].failed = false;
      state[key].error = null;
    })
    .addCase(thunk.rejected, (state, action) => {
      state[key].processing = false;
      state[key].done = false;
      state[key].failed = true;
      state[key].error = action.payload;
    });
};

const addFetchCases = (builder, thunk, key) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[key].fetching = true;
      state[key].fetchingSuccessful = false;
      state[key].fetchingFailed = false;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[key].fetching = false;
      state[key].fetchingSuccessful = true;
      state[key].fetchingFailed = false;
      state[key].data = action.payload;
    })
    .addCase(thunk.rejected, (state) => {
      state[key].fetching = false;
      state[key].fetchingSuccessful = false;
      state[key].fetchingFailed = true;
    });
};

const discoverySlice = createSlice({
  name: 'discovery',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addMutationCases(builder, createDiscoveryTree, 'createDiscoveryTree');
    addFetchCases(builder, getDiscoveryTreeStatus, 'discoveryTreeStatus');
    addFetchCases(builder, getSellingStyles, 'sellingStyles');
    addMutationCases(builder, generateValueProp, 'generateValueProp');
    addFetchCases(builder, checkValuePropStatus, 'valuePropGenerationStatus');
    addFetchCases(builder, getDealDiscoveryRisks, 'dealDiscoveryRisks');
    addMutationCases(builder, createMeetingPrep, 'createMeetingPrep');
    addMutationCases(builder, createMeetingPrepGoals, 'createMeetingPrepGoals');
    addFetchCases(builder, getOrgChart, 'orgChart');
    addMutationCases(builder, createOrUpdateOrgChart, 'updateOrgChart');
    addFetchCases(builder, getOrgChartForDeal, 'orgChartForDeal');
    addMutationCases(builder, updateOrgChartForDeal, 'updateOrgChartForDeal');
    addFetchCases(builder, getValuePropFiles, 'valuePropFiles');
    addMutationCases(builder, deleteValuePropFile, 'deleteValuePropFile');
    addFetchCases(builder, getUploadUrlForFile, 'uploadFileUrl');
    addMutationCases(
      builder,
      updateValuePropFileTitle,
      'updateValuePropFileTitle',
    );
    addMutationCases(builder, confirmFileUpload, 'confirmFileUpload');
  },
});

export default discoverySlice.reducer;
